/**
 * Build Redux WorkflowCalculationResults + report payload from Stage 4 solve.
 * Single source for Recipe finalize → Results (no Stage 5 re-solve).
 */
import type { WorkflowCalculationResults } from '../store/slices/workflowSlice';

export function buildWorkflowResultsFromStage4(args: {
  stockCalculationResult: any;
  formulation: any;
  waterProfile: any;
  stockVolume: number;
  dilutionRatio: number;
  workingSolutionVolume?: number;
  phAdjustmentResult?: any;
  abSeparationResult?: any;
}): {
  calculationResults: WorkflowCalculationResults;
  reportData: any;
} {
  const {
    stockCalculationResult: r,
    formulation,
    waterProfile,
    stockVolume,
    dilutionRatio,
    workingSolutionVolume,
    phAdjustmentResult,
    abSeparationResult,
  } = args;

  const saltAmounts = (r?.saltAmounts || []).map((salt: any) => {
    let tankAssignment = salt.tankAssignment || 'A';
    if (abSeparationResult) {
      const inA = abSeparationResult.tankA?.salts?.some(
        (s: any) => s.id === salt.id
      );
      tankAssignment = inA ? 'A' : 'B';
    }
    return {
      ...salt,
      tankAssignment,
      unit: salt.unit || 'g',
    };
  });

  const achieved =
    r?.achievedConcentrations ||
    r?.predictions?.achievedConcentrations ||
    [];

  const ec =
    r?.solutionProperties?.electricalConductivity ??
    r?.stockSolution?.estimatedEC ??
    0;
  const ph =
    phAdjustmentResult?.finalPH ??
    r?.solutionProperties?.finalPH ??
    r?.stockSolution?.estimatedPH ??
    6.5;
  const tds =
    r?.solutionProperties?.totalDissolvedSolids ??
    r?.stockSolution?.totalDissolvedSolids ??
    0;

  const calculationResults: WorkflowCalculationResults = {
    recipe: {
      salts: saltAmounts.map((s: any) => ({
        name: s.name,
        amount: Number(s.amount) || 0,
        unit: s.unit || 'g',
        id: s.id,
        formula: s.formula,
        elements: s.elements,
        purity: s.purity,
        tankAssignment: s.tankAssignment,
      })) as any,
      saltAmounts: saltAmounts as any,
      phAdjustment: phAdjustmentResult
        ? {
            product: phAdjustmentResult.requiredAgent?.name || 'Acid',
            amount: Number(phAdjustmentResult.volumeRequired) || 0,
            unit: 'mL',
          }
        : { product: '', amount: 0, unit: 'mL' },
      totalCost: 0,
      preparationTime: 0,
    } as any,
    predictions: {
      finalPh: ph,
      finalEc: ec,
      achievedConcentrations: achieved.map((e: any) => ({
        symbol: e.symbol || e.element?.symbol || '',
        target: Number(e.target ?? 0),
        achieved: Number(e.achieved ?? 0),
      })),
      ionicStrength: r?.solutionProperties?.ionicStrength,
      totalDissolvedSolids: tds,
    },
    instructions: {
      stockPreparation: r?.preparationSteps || [],
      workingSolution: [
        `Dilute 1:${dilutionRatio} (use ${Math.round(1000 / Math.max(1, dilutionRatio))} mL stock per L final)`,
      ],
      qualityChecks: r?.qualityChecks || [],
    },
    hydroBuddyAnalysis: {
      errorAnalysis: {
        overallAccuracy: 'good',
        maxError: 0,
        meanAbsoluteError: 0,
        rootMeanSquareError: 0,
      },
      metadata: {
        matrixCondition: r?.metadata?.matrixCondition || 0,
        solverMethod: r?.metadata?.solverMethod || 'stage4-dss',
        calculationTime: r?.metadata?.calculationTime || 0,
        warnings: r?.warnings || [],
      },
      matrixCondition: r?.metadata?.matrixCondition || 0,
      solverMethod: r?.metadata?.solverMethod || 'stage4-dss',
    },
  };

  const reportData = {
    metadata: {
      recipeName: `${formulation?.name || 'Custom'} Recipe`,
      cropType: formulation?.cropType || 'General',
      growthStage: 'General',
      targetEC: ec || 2.0,
      targetPH: ph,
      waterSource: waterProfile?.name || 'Unknown',
      createdDate: new Date().toISOString(),
      targetVolume: stockVolume,
      concentrationUnit: 'mg/L',
      massUnit: 'g',
      stockVolumePerTank: stockVolume,
      dilutionRatio,
      workingVolumeTarget:
        workingSolutionVolume || stockVolume * dilutionRatio,
      modelVersion: 'FlahaFast DSS',
      calculationEngine: 'HydroBuddy matrix (Stage 4)',
    },
    formulation: {
      name: formulation?.name || 'Custom',
      elements: (formulation?.elements || [])
        .map((e: any) => ({
          symbol: e.symbol || e.element?.symbol || e.elementSymbol || '',
          concentration: Number(
            e.concentration ?? e.targetConcentration ?? e.target ?? 0
          ),
        }))
        .filter((e: any) => e.symbol),
    },
    waterProfile: {
      name: waterProfile?.name || 'Unknown',
      elements: waterProfile?.elements || [],
    },
    saltAmounts,
    achievedConcentrations: achieved
      .map((e: any) => ({
        symbol: e.symbol || e.element?.symbol || '',
        target: Number(e.target ?? 0),
        achieved: Number(e.achieved ?? 0),
        difference: Number(
          e.difference ?? e.error ?? (e.achieved ?? 0) - (e.target ?? 0)
        ),
      }))
      .filter((e: any) => e.symbol),
    solutionProperties: {
      electricalConductivity: ec,
      finalPH: ph,
      totalDissolvedSolids: tds,
      ionicStrength: r?.solutionProperties?.ionicStrength,
    },
    phAdjustment: phAdjustmentResult
      ? {
          agent: phAdjustmentResult.requiredAgent?.name,
          volume: phAdjustmentResult.volumeRequired,
          finalPH: phAdjustmentResult.finalPH,
        }
      : undefined,
    recipe: calculationResults.recipe,
    predictions: calculationResults.predictions,
    instructions: calculationResults.instructions,
  };

  return { calculationResults, reportData };
}
